<template>
  <v-main>
    <v-container fluid>
      <!-- Combined Header and Filtration Section -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Page Header -->
            <page-header
              :title="isDeletePage() != true ? 'Seller Product Variant Management' : 'Deleted Seller Product Variants'"
              :subtitle="isDeletePage() != true ? 'Manage and monitor all seller product variants' : 'Restore or permanently delete seller product variants'"
            >
              <template v-slot:actions>
                <div v-if="isDeletePage() != true" class="d-flex justify-end align-center">
                    <template v-if="!isSeller">
                      <v-btn
                        color="info"
                        elevation="1"
                        small
                        to="/seller-product-variants/upload"
                        class="mr-2 px-3"
                        outlined
                      >
                        <v-icon left x-small>mdi-upload</v-icon>
                        Bulk Upload
                      </v-btn>
                      <v-btn
                        color="orange"
                        elevation="1"
                        small
                        @click="openUploadHistory"
                        class="mr-2 px-3"
                        outlined
                      >
                        <v-icon left x-small>mdi-history</v-icon>
                        Upload History
                      </v-btn>
                      <v-btn
                        color="primary"
                        elevation="1"
                        small
                        to="/seller-product-variants/create"
                        class="mr-2 px-3"
                      >
                        <v-icon left x-small>mdi-plus</v-icon>
                        Add Variant
                      </v-btn>
                    </template>
                  </div>
              </template>
            </page-header>

            <!-- Filtration Section -->
            <v-card-subtitle class="pb-1 pt-2 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-filter-variant</v-icon
                    >
                    <span class="text-body-2 font-weight-medium"
                      >Filter Seller Product Variants</span
                    >
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip
                      v-if="hasActiveFilters"
                      color="info"
                      x-small
                      class="mr-2"
                    >
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active
                    </v-chip>
                    <v-btn
                      text
                      x-small
                      color="error"
                      @click="clearAllFilters"
                      :disabled="!hasActiveFilters"
                    >
                      <v-icon left x-small>mdi-filter-remove</v-icon>
                      Clear
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-4 pb-4">
              <v-row>
                <!-- Search Field -->
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="filter.search_keyword"
                    label="Search Seller Products"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    clearable
                    placeholder="Seller name, product name..."
                    hide-details
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>

                <!-- Status Filter -->
                <v-col cols="12" md="2">
                  <v-autocomplete
                    v-model="filter.is_active"
                    :items="statuses"
                    label="Status"
                    prepend-inner-icon="mdi-toggle-switch"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- User Filter -->
                <v-col cols="12" md="2" v-if="!isSeller">
                  <v-autocomplete
                    v-model="filter.seller_id"
                    :items="users"
                    item-text="name"
                    item-value="id"
                    label="Seller"
                    prepend-inner-icon="mdi-account"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- Brand Filter -->
                <v-col cols="12" md="2">
                  <v-autocomplete
                    v-model="filter.brand_name"
                    :items="brands"
                    item-text="description"
                    item-value="id"
                    label="Brand"
                    prepend-inner-icon="mdi-tag"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- Category Filter -->
                <v-col cols="12" md="2">
                  <v-autocomplete
                    v-model="filter.category_id"
                    :items="categories"
                    item-text="name"
                    item-value="id"
                    label="Category"
                    prepend-inner-icon="mdi-shape"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- Stock Filter -->
                <v-col cols="12" md="2">
                  <v-autocomplete
                    v-model="filter.stock_filter"
                    :items="stockFilterItems"
                    label="Stock Level"
                    prepend-inner-icon="mdi-package"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Combined Data Table Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl">
            <!-- Card Header with Title and Controls -->
            <v-card-title class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <!-- Title Section -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-table</v-icon
                    >
                    <span class="text-body-1 font-weight-medium"
                      >Seller Product Variants List</span
                    >
                  </div>
                </v-col>

                <!-- Results Summary Section -->
                <v-col cols="12" md="5" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <v-chip color="primary" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-package-variant</v-icon>
                      {{ count }}
                      {{ count === 1 ? "Variant" : "Variants" }}
                    </v-chip>
                    <span class="text-caption grey--text" v-if="count > 0">
                      {{ (page - 1) * itemsPerPage + 1 }}-{{
                        Math.min(page * itemsPerPage, count)
                      }}
                      of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Controls Section -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Items per page selector -->
                    <div class="d-flex align-center mr-2">
                      <span class="text-caption grey--text mr-1">Show:</span>
                      <v-btn-toggle
                        v-model="selectedItemsPerPage"
                        dense
                        class="mr-2"
                        @change="changeItemsPerPageToggle"
                      >
                        <v-btn x-small outlined :value="10">10</v-btn>
                        <v-btn x-small outlined :value="25">25</v-btn>
                        <v-btn x-small outlined :value="50">50</v-btn>
                      </v-btn-toggle>
                    </div>

                    <!-- Bulk delete button shown when items are selected -->
                    <v-btn
                      v-if="selectedItems.length > 0 && !isDeletePage() && !isSeller"
                      color="error"
                      x-small
                      class="mr-2"
                      @click="confirmBulkDelete"
                    >
                      <v-icon left x-small>mdi-delete-sweep</v-icon>
                      Delete ({{ selectedItems.length }})
                    </v-btn>

                    <!-- Export Button -->
                    <v-btn
                      v-if="!isDeletePage() && seller_products.length > 0"
                      x-small
                      color="success"
                      outlined
                      @click="openExportDialog"
                      class="px-2"
                    >
                      <v-icon left x-small>mdi-microsoft-excel</v-icon>
                      Export
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <!-- Table Content -->
            <v-card-text class="pa-0">
              <!-- Loading State -->
              <section
                v-if="isLoading"
                style="height: 400px"
                class="d-flex align-center justify-center"
              >
                <div class="text-center">
                  <health-loader message="Loading seller product variants..." />
                </div>
              </section>

              <!-- Data Table -->
              <v-data-table
                v-else
                v-model="selectedItems"
                :headers="headers"
                :items="seller_products"
                :server-items-length="count"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                hide-default-footer
                show-select
                item-key="id"
                class="enhanced-table"
                fixed-header
              >
                <!-- Serial Number Column -->
                <template #[`item.sr_no`]="{ index }">
                  <span class="font-weight-medium primary--text">
                    {{ (page - 1) * itemsPerPage + index + 1 }}
                  </span>
                </template>

                <!-- Seller Code Column -->
                <template #[`item.seller_code`]="{ item }">
                  <div class="text-body-2">
                    {{ item.seller && item.seller.guest_code ? item.seller.guest_code : "N/A" }}
                  </div>
                </template>

                <!-- Seller Column -->
                <template #[`item.seller`]="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="32" class="mr-2" color="primary">
                      <span class="white--text text-caption">
                        {{ getSellerInitials(item.seller) }}
                      </span>
                    </v-avatar>
                    <div>
                      <div class="text-body-2 font-weight-medium">
                        {{ getSellerName(item.seller) }}
                      </div>
                      <div class="text-caption grey--text">
                        {{ item.seller ? item.seller.email : "N/A" }}
                      </div>
                    </div>
                  </div>
                </template>

                <!-- SKU Column -->
                <template #[`item.sku`]="{ item }">
                  <div class="text-body-2">
                    {{ item.product_variant ? item.product_variant.sku_id : "N/A" }}
                  </div>
                </template>

                <!-- Product Variant Column -->
                <template #[`item.product_variant`]="{ item }">
                  <div class="text-body-2 font-weight-medium">
                    {{ item.product_variant ? item.product_variant.name : "N/A" }}
                  </div>
                </template>

                <!-- Brand Column -->
                <template #[`item.brand`]="{ item }">
                  <div class="text-body-2" v-if="item.seller_product && item.seller_product.product">
                    {{ item.seller_product.product.brand_name || "N/A" }}
                  </div>
                  <span v-else class="text-caption grey--text">N/A</span>
                </template>

                <!-- Category Column -->
                <template #[`item.category`]="{ item }">
                  <div class="text-body-2">
                    {{ item.seller_product && item.seller_product.product && item.seller_product.product.category ? item.seller_product.product.category.name : "N/A" }}
                  </div>
                </template>

                <!-- HSN Code Column -->
                <template #[`item.hsn_code`]="{ item }">
                  <div class="text-body-2">
                    {{ item.seller_product && item.seller_product.product && item.seller_product.product.hsn_code ? item.seller_product.product.hsn_code : "N/A" }}
                  </div>
                </template>

                <!-- Country Column -->
                <template #[`item.country`]="{ item }">
                  <div class="text-body-2">
                    {{ item.product_variant && item.product_variant.country ? item.product_variant.country : "N/A" }}
                  </div>
                </template>

                <!-- Manufacturer Column -->
                <template #[`item.manufacturer`]="{ item }">
                  <div class="text-body-2">
                    {{ item.product_variant && item.product_variant.manufacturer ? item.product_variant.manufacturer : "N/A" }}
                  </div>
                </template>

                <!-- MRP Column -->
                <template #[`item.mrp`]="{ item }">
                  <div class="text-body-2 font-weight-medium">
                    ₹{{ Number(item.maximum_retail_price || 0).toFixed(2) }}
                  </div>
                </template>

                <!-- Stock Column -->
                <template #[`item.stock`]="{ item }">
                  <div class="text-body-2">
                    {{ item.stock }}
                  </div>
                </template>

                <!-- Seller Discount Column -->
                <template #[`item.seller_discount`]="{ item }">
                  <div class="text-body-2">
                    <template v-if="item.seller_discount">
                      ₹{{ ((Number(item.maximum_retail_price || 0) * Number(item.seller_discount)) / 100).toFixed(2) }}
                      <div class="text-caption grey--text">
                        ({{ item.seller_discount }}%)
                      </div>
                    </template>
                    <template v-else>N/A</template>
                  </div>
                </template>

                <!-- HT Discount Column -->
                <template #[`item.ht_discount`]="{ item }">
                  <div class="text-body-2">
                    <template v-if="item.ht_discount">
                      ₹{{ ((Number(item.maximum_retail_price || 0) * Number(item.ht_discount)) / 100).toFixed(2) }}
                      <div class="text-caption grey--text">
                        ({{ item.ht_discount }}%)
                      </div>
                    </template>
                    <template v-else>N/A</template>
                  </div>
                </template>

                <!-- Consumer Discount Column -->
                <template #[`item.consumer_discount`]="{ item }">
                  <div class="text-body-2">
                    <template v-if="item.consumer_discount">
                      ₹{{ ((Number(item.maximum_retail_price || 0) * Number(item.consumer_discount)) / 100).toFixed(2) }}
                      <div class="text-caption grey--text">
                        ({{ item.consumer_discount }}%)
                      </div>
                    </template>
                    <template v-else>N/A</template>
                  </div>
                </template>

                <!-- Doctor Discount Column -->
                <template #[`item.doctor_discount`]="{ item }">
                  <div class="text-body-2">
                    <template v-if="item.doctor_discount">
                      ₹{{ ((Number(item.maximum_retail_price || 0) * Number(item.doctor_discount)) / 100).toFixed(2) }}
                      <div class="text-caption grey--text">
                        ({{ item.doctor_discount }}%)
                      </div>
                    </template>
                    <template v-else>N/A</template>
                  </div>
                </template>

                <!-- Created By Column -->
                <template #[`item.created_by`]="{ item }">
                  <div v-if="item.created_by" class="text-body-2">
                    {{ item.created_by.first_name }} {{ item.created_by.last_name }}
                  </div>
                  <div v-else class="text-caption grey--text">System</div>
                </template>

                <!-- Created Date Column -->
                <template #[`item.created_at`]="{ item }">
                  <div class="text-caption">
                    {{ formatDate(item.created_at) }}
                  </div>
                </template>

                <!-- Actions Column -->
                <template #[`item.actions`]="{ item }">
                  <div class="d-flex justify-center align-center" style="gap:4px">
                    <template v-if="!isDeletePage()">
                      <!-- Edit Button -->
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="primary"
                            :to="`/seller-product-variants/${encryptIt(item.id)}`"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small>mdi-pencil</v-icon>
                          </v-btn>
                        </template>
                        <span>Edit Seller Product</span>
                      </v-tooltip>

                      <!-- Delete Button (admin only) -->
                      <v-tooltip bottom v-if="!isSeller">
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="error"
                            @click="showDeleteConfirmation(item)"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small>mdi-delete</v-icon>
                          </v-btn>
                        </template>
                        <span>Delete Seller Product</span>
                      </v-tooltip>
                    </template>

                    <template v-else>
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="success"
                            @click="Restore_this(item.id)"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small left>mdi-restore</v-icon>
                            Restore
                          </v-btn>
                        </template>
                        <span>Restore Seller Product</span>
                      </v-tooltip>
                    </template>
                  </div>
                </template>

                <!-- Empty State -->
                <template #no-data>
                  <div class="text-center py-12">
                    <v-icon size="80" color="grey lighten-3"
                      >mdi-shopping-search</v-icon
                    >
                    <h3 class="text-h6 grey--text mt-4 mb-2">
                      No Seller Products Found
                    </h3>
                    <p class="grey--text mb-4">
                      {{
                        hasActiveFilters
                          ? "No seller products match your current filters. Try adjusting your search criteria."
                          : "You haven't added any seller products yet."
                      }}
                    </p>
                    <v-btn
                      v-if="!hasActiveFilters && !isDeletePage()"
                      color="primary"
                      to="/seller-products/create"
                    >
                      <v-icon left>mdi-plus</v-icon>
                      Add Your First Seller Product
                    </v-btn>
                    <v-btn
                      v-else-if="hasActiveFilters"
                      color="primary"
                      outlined
                      @click="clearAllFilters"
                    >
                      <v-icon left>mdi-filter-remove</v-icon>
                      Clear Filters
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>

            <!-- Integrated Pagination Footer -->
            <v-divider></v-divider>
            <v-card-actions class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <!-- Pagination Info -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <span class="text-caption grey--text">
                      {{ (page - 1) * itemsPerPage + 1 }}-{{
                        Math.min(page * itemsPerPage, count)
                      }}
                      of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Pagination Controls -->
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <!-- Previous Button -->
                    <v-btn
                      x-small
                      outlined
                      :disabled="page <= 1"
                      @click="page--"
                      class="mr-2"
                    >
                      <v-icon x-small left>mdi-chevron-left</v-icon>
                      Prev
                    </v-btn>

                    <!-- Page Numbers -->
                    <v-pagination
                      v-model="page"
                      :length="paginationLength"
                      :total-visible="5"
                      class="compact-pagination mx-1"
                    ></v-pagination>

                    <!-- Next Button -->
                    <v-btn
                      x-small
                      outlined
                      :disabled="page >= paginationLength"
                      @click="page++"
                      class="ml-2"
                    >
                      Next
                      <v-icon x-small right>mdi-chevron-right</v-icon>
                    </v-btn>
                  </div>
                </v-col>

                <!-- Jump to Page -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <span class="text-caption grey--text mr-2">Go to:</span>
                    <v-text-field
                      v-model="jumpToPage"
                      type="number"
                      :min="1"
                      :max="paginationLength"
                      dense
                      outlined
                      hide-details
                      hide-spin-buttons
                      class="no-spin-arrows compact-input"
                      style="max-width: 60px"
                      @keydown.enter="goToPage"
                      @blur="goToPage"
                    ></v-text-field>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Delete Success Dialog -->
      <v-dialog v-model="is_deleted_seller_product" width="600">
        <v-card>
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-empty
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Seller Product deleted successfully.</h3>
                      <br />
                      <v-btn
                        color="success"
                        @click="is_deleted_seller_product = false"
                      >
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>

      <!-- Restore Success Dialog -->
      <v-dialog v-model="is_restored_seller_product" width="600">
        <v-card>
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-restore
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Seller Product restored successfully.</h3>
                      <br />
                      <v-btn
                        color="success"
                        @click="is_restored_seller_product = false"
                      >
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="showDeleteDialog" max-width="500" persistent>
        <v-card>
          <v-card-title class="headline error white--text d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-alert-circle</v-icon>
            Confirm Delete
          </v-card-title>

          <v-card-text class="pt-4">
            <div v-if="sellerProductToDelete" style="background:#f5f5f5;border-radius:8px" class="pa-3 mb-3">
              <div class="d-flex justify-space-between mb-1">
                <span class="text-caption grey--text">Product Variant</span>
                <span class="text-caption font-weight-bold">{{ sellerProductToDelete.product_variant ? sellerProductToDelete.product_variant.name : 'N/A' }}</span>
              </div>
              <div class="d-flex justify-space-between mb-1">
                <span class="text-caption grey--text">Seller</span>
                <span class="text-caption font-weight-bold">
                  {{ sellerProductToDelete.seller ? ((sellerProductToDelete.seller.first_name || '') + ' ' + (sellerProductToDelete.seller.last_name || '')).trim() || 'N/A' : 'N/A' }}
                </span>
              </div>
              <div class="d-flex justify-space-between">
                <span class="text-caption grey--text">Stock (Qty)</span>
                <span class="text-caption font-weight-bold">{{ sellerProductToDelete.stock ?? 'N/A' }}</span>
              </div>
            </div>

            <v-textarea
              v-model="deleteRemarks"
              outlined
              dense
              label="Remarks (optional)"
              rows="2"
              hide-details="auto"
              class="mb-3"
            ></v-textarea>

            <p class="text-body-2 grey--text mb-0">
              <strong>Warning:</strong> This moves the product to trash. You can restore it later.
            </p>
          </v-card-text>

          <v-card-actions class="px-4 pb-4">
            <v-spacer></v-spacer>
            <v-btn color="grey darken-1" text @click="cancelDelete" :disabled="isDeleting">
              <v-icon left>mdi-close</v-icon>Cancel
            </v-btn>
            <v-btn color="error" @click="confirmDelete" :loading="isDeleting" :disabled="isDeleting">
              <v-icon left>mdi-delete</v-icon>Delete
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Bulk Delete Confirmation Dialog -->
      <v-dialog v-model="bulkDeleteDialog.show" max-width="420" persistent>
        <v-card class="rounded-xl">
          <v-card-title class="pa-2">
            <v-icon color="error" class="mr-2">mdi-delete-sweep</v-icon>
            <span class="text-h6">Confirm Bulk Delete</span>
          </v-card-title>

          <v-card-text class="pa-2">
            <p class="mb-2">
              Are you sure you want to delete
              <strong>{{ selectedItems.length }} selected variant(s)</strong>?
              This moves them to trash and can be restored later.
            </p>
          </v-card-text>

          <v-card-actions class="pa-2">
            <v-spacer></v-spacer>
            <v-btn text @click="bulkDeleteDialog.show = false">Cancel</v-btn>
            <v-btn
              color="error"
              @click="bulkDeleteItems"
              :loading="bulkDeleteDialog.loading"
            >
              Delete {{ selectedItems.length }} Item(s)
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Export Dialog -->
      <v-dialog v-model="showExportDialog" max-width="700" persistent>
        <v-card>
          <v-card-title class="headline success white--text d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-microsoft-excel</v-icon>
            Export Seller Products
          </v-card-title>

          <v-card-text class="pt-4">
            <v-row>
              <v-col cols="12">
                <p class="text-body-2 grey--text mb-3">
                  Apply filters to customize your export data
                </p>
              </v-col>

              <!-- Product Variant Name -->
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="exportFilter.product_variant_id"
                  :items="productVariants"
                  item-text="name"
                  item-value="id"
                  label="Product Variant"
                  prepend-inner-icon="mdi-package-variant"
                  outlined
                  dense
                  clearable
                  hide-details
                  placeholder="Select product variant..."
                  :loading="loadingProductVariants"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>{{ item.name }}</v-list-item-title>
                      <v-list-item-subtitle class="text-caption">
                        {{ item.product ? item.product.name : '' }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>

              <!-- Brand -->
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="exportFilter.brand_name"
                  :items="brands"
                  item-text="description"
                  item-value="id"
                  label="Brand"
                  prepend-inner-icon="mdi-tag"
                  outlined
                  dense
                  clearable
                  hide-details
                  placeholder="Select brand..."
                  :loading="loadingBrands"
                ></v-autocomplete>
              </v-col>

              <!-- Category -->
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="exportFilter.category_id"
                  :items="categories"
                  item-text="name"
                  item-value="id"
                  label="Category"
                  prepend-inner-icon="mdi-shape"
                  outlined
                  dense
                  clearable
                  hide-details
                  placeholder="Select category..."
                  :loading="loadingCategories"
                ></v-autocomplete>
              </v-col>

              <!-- Tag -->
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="exportFilter.tag_id"
                  :items="tags"
                  item-text="name"
                  item-value="id"
                  label="Tag"
                  prepend-inner-icon="mdi-label"
                  outlined
                  dense
                  clearable
                  hide-details
                  placeholder="Select tag..."
                  :loading="loadingTags"
                ></v-autocomplete>
              </v-col>

              <!-- From Date -->
              <v-col cols="12" md="6">
                <v-menu
                  v-model="fromDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="exportFilter.from_date"
                      label="From Date"
                      prepend-inner-icon="mdi-calendar"
                      outlined
                      dense
                      clearable
                      hide-details
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      placeholder="Select from date..."
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="exportFilter.from_date"
                    @input="fromDateMenu = false"
                    no-title
                    scrollable
                  ></v-date-picker>
                </v-menu>
              </v-col>

              <!-- To Date -->
              <v-col cols="12" md="6">
                <v-menu
                  v-model="toDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="exportFilter.to_date"
                      label="To Date"
                      prepend-inner-icon="mdi-calendar"
                      outlined
                      dense
                      clearable
                      hide-details
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      placeholder="Select to date..."
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="exportFilter.to_date"
                    @input="toDateMenu = false"
                    :min="exportFilter.from_date"
                    no-title
                    scrollable
                  ></v-date-picker>
                </v-menu>
              </v-col>

              <v-col cols="12" class="pt-0">
                <v-alert
                  v-if="exportFilter.from_date && exportFilter.to_date && exportFilter.from_date > exportFilter.to_date"
                  type="error"
                  dense
                  text
                  class="mb-0"
                >
                  "From Date" cannot be later than "To Date"
                </v-alert>
              </v-col>
            </v-row>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="px-4 py-3">
            <v-btn
              text
              color="grey darken-1"
              @click="clearExportFilters"
              small
            >
              <v-icon left small>mdi-filter-remove</v-icon>
              Clear Filters
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              text
              color="grey darken-1"
              @click="closeExportDialog"
              :disabled="isExcelloading"
              small
            >
              <v-icon left small>mdi-close</v-icon>
              Cancel
            </v-btn>
            <v-btn
              color="success"
              @click="generateExcel"
              :loading="isExcelloading"
              :disabled="isExcelloading || (exportFilter.from_date && exportFilter.to_date && exportFilter.from_date > exportFilter.to_date)"
              small
            >
              <v-icon left small>mdi-download</v-icon>
              Export Excel
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Upload History Dialog -->
      <SellerProductVariantUploadHistory
        v-if="showUploadHistoryDialog"
        v-model="showUploadHistoryDialog"
        @refresh-needed="getData"
      />
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";
import SellerProductVariantUploadHistory from "@/components/SellerProductVariantUploadHistory.vue";

export default {
  name: "SellerProductsPage",
  components: {
    SellerProductVariantUploadHistory,
  },
  data() {
    return {
      filter: {
        search_keyword: "",
        is_active: "",
        seller_id: "",
        brand_name: "",
        category_id: "",
        stock_filter: "",
        show_deleted: false,
      },
      page: 1,
      itemsPerPage: 10,
      selectedItemsPerPage: 10,
      serialNoStarting: 1,
      paginationLength: 0,
      count: 0,
      jumpToPage: 1,
      is_deleted_seller_product: false,
      is_restored_seller_product: false,
      showDeleteDialog: false,
      sellerProductToDelete: null,
      deleteRemarks: '',
      isDeleting: false,
      selectedItems: [],
      bulkDeleteDialog: {
        show: false,
        loading: false,
      },
      // Dialog states
      showUploadHistoryDialog: false,
      showExportDialog: false,
      // Export filter
      exportFilter: {
        product_variant_id: null,
        brand_name: null,
        category_id: null,
        tag_id: null,
        from_date: null,
        to_date: null,
      },
      // Date pickers
      fromDateMenu: false,
      toDateMenu: false,
      // Master data
      seller_products: [],
      users: [],
      products: [],
      productVariants: [],
      brands: [],
      categories: [],
      tags: [],
      loadingProductVariants: false,
      loadingBrands: false,
      loadingCategories: false,
      loadingTags: false,
      statuses: [
        { text: "Active", value: "1" },
        { text: "Inactive", value: "0" },
      ],
      stockFilterItems: [
        { text: "In Stock", value: "in_stock" },
        { text: "Out of Stock", value: "out_of_stock" },
        { text: "Low Stock", value: "low_stock" },
      ],
      baseHeaders: [
        {
          text: "#",
          value: "sr_no",
          sortable: false,
          width: "60px",
          align: "center",
        },
        {
          text: "Seller Code",
          value: "seller_code",
          sortable: false,
          width: "120px",
        },
        {
          text: "Seller",
          value: "seller",
          sortable: false,
          width: "180px",
        },
        {
          text: "SKU",
          value: "sku",
          sortable: false,
          width: "140px",
        },
        {
          text: "Product Variant",
          value: "product_variant",
          sortable: false,
          width: "180px",
        },
        {
          text: "Brand",
          value: "brand",
          sortable: false,
          width: "120px",
        },
        {
          text: "Category",
          value: "category",
          sortable: false,
          width: "130px",
        },
        {
          text: "HSN Code",
          value: "hsn_code",
          sortable: false,
          width: "110px",
        },
        {
          text: "Country",
          value: "country",
          sortable: false,
          width: "110px",
        },
        {
          text: "Manufacturer",
          value: "manufacturer",
          sortable: false,
          width: "160px",
        },
        {
          text: "MRP",
          value: "mrp",
          sortable: false,
          width: "100px",
        },
        {
          text: "Stock",
          value: "stock",
          sortable: false,
          width: "100px",
        },
      ],
      isLoading: false,
      isExcelloading: false,
    };
  },

  computed: {
    isSeller() {
      const pos = this.userPosition && this.userPosition.name ? this.userPosition.name.toUpperCase() : '';
      return pos === 'SELLER';
    },

    hasActiveFilters() {
      return (
        this.filter.search_keyword ||
        this.filter.is_active ||
        this.filter.seller_id ||
        this.filter.brand_name ||
        this.filter.category_id ||
        this.filter.stock_filter
      );
    },

    // Dynamically build headers based on user position
    headers() {
      let headers = this.isSeller
        ? this.baseHeaders.filter(h => h.value !== 'seller_code' && h.value !== 'seller')
        : [...this.baseHeaders];

      // Determine which discount columns to show based on user position
      const position = this.userPosition?.name?.toUpperCase();
      const isAdminUser = this.isAdmin || this.isSuperAdmin;

      // Add discount columns based on position
      if (isAdminUser) {
        // Admin sees all discounts
        headers.push(
          { text: "Seller Discount %", value: "seller_discount", sortable: false, width: "130px" },
          { text: "HT Discount %", value: "ht_discount", sortable: false, width: "120px" },
          { text: "Consumer Discount %", value: "consumer_discount", sortable: false, width: "150px" },
          { text: "Doctor Discount %", value: "doctor_discount", sortable: false, width: "140px" }
        );
      } else if (position === 'SELLER') {
        headers.push(
          { text: "Seller Discount %", value: "seller_discount", sortable: false, width: "130px" }
        );
      } else if (position === 'DOCTOR') {
        headers.push(
          { text: "Doctor Discount %", value: "doctor_discount", sortable: false, width: "140px" }
        );
      } else if (position === 'CONSUMER') {
        headers.push(
          { text: "Consumer Discount %", value: "consumer_discount", sortable: false, width: "150px" }
        );
      }

      // Add remaining columns
      headers.push(
        { text: "Created By", value: "created_by", sortable: false, width: "140px" },
        { text: "Created Date", value: "created_at", sortable: false, width: "150px" },
        { text: "Actions", value: "actions", sortable: false, width: "120px", align: "center" }
      );

      return headers;
    },
  },

  watch: {
    page: {
      handler: function (newPage) {
        this.jumpToPage = newPage;
        this.getData();
      },
    },
    itemsPerPage: "getData",
    $route() {
      this.getData();
    },
  },

  mounted() {
    this.jumpToPage = this.page;
    this.getData();
    this.getMasters();
  },

  methods: {
    async getData() {
      console.log("Get Seller Product Variants Data");
      this.filter.show_deleted = this.$route.query.open_trash ? 1 : 0;
      this.isLoading = true;

      // Ensure all filter values are properly set
      this.filter.search_keyword = this.filter.search_keyword || "";
      this.filter.is_active = this.filter.is_active || "";
      this.filter.seller_id = this.filter.seller_id || "";
      this.filter.brand_name = this.filter.brand_name || "";
      this.filter.category_id = this.filter.category_id || "";
      this.filter.stock_filter = this.filter.stock_filter || "";

      // Build query params using URLSearchParams
      const params = new URLSearchParams({
        page: this.page,
        rowsPerPage: this.itemsPerPage,
        ...this.filter,
      });

      try {
        const response = await axios.get(`/seller_product_variants?${params}`);
        this.seller_products = response.data.data;
        this.count = response.data.count;
        this.serialNoStarting = (this.page - 1) * this.itemsPerPage;
        this.paginationLength = Math.ceil(this.count / this.itemsPerPage);
        this.selectedItems = [];
      } catch (error) {
        console.log(error);
        if (error.response && error.response.status === 401) {
          this.$router.push('/login');
        } else {
          this.$store.dispatch('errors/setError', {
            message: 'Failed to load seller product variants'
          });
        }
      }

      this.isLoading = false;
    },

    showDeleteConfirmation(sellerProduct) {
      this.sellerProductToDelete = sellerProduct;
      this.showDeleteDialog = true;
    },

    cancelDelete() {
      this.showDeleteDialog = false;
      this.sellerProductToDelete = null;
      this.deleteRemarks = '';
      this.isDeleting = false;
    },

    async confirmDelete() {
      if (!this.sellerProductToDelete) return;

      this.isDeleting = true;

      try {
        await axios.post(
          `/seller_product_variants/delete/${this.encryptIt(this.sellerProductToDelete.id)}`,
          { remarks: this.deleteRemarks || undefined }
        );
        this.showDeleteDialog = false;
        this.sellerProductToDelete = null;
        this.deleteRemarks = '';
        this.isDeleting = false;
        this.is_deleted_seller_product = true;
        this.getData();
      } catch (error) {
        console.log(error);
        this.isDeleting = false;
        if (error.response && error.response.status === 401) {
          this.$router.push('/login');
        } else {
          this.$store.dispatch('errors/setError', {
            message: 'Failed to delete seller product'
          });
        }
      }
    },

    async Delete_this(id) {
      try {
        await axios.post(`/seller_product_variants/delete/${this.encryptIt(id)}`);
        this.is_deleted_seller_product = true;
        this.getData();
      } catch (error) {
        console.log(error);
        if (error.response && error.response.status === 401) {
          this.$router.push('/login');
        } else {
          this.$store.dispatch('errors/setError', {
            message: 'Failed to delete seller product'
          });
        }
      }
    },

    async Restore_this(id) {
      try {
        await axios.post(`/seller_product_variants/restore/${this.encryptIt(id)}`);
        this.is_restored_seller_product = true;
        this.getData();
      } catch (error) {
        console.log(error);
        if (error.response && error.response.status === 401) {
          this.$router.push('/login');
        } else {
          this.$store.dispatch('errors/setError', {
            message: 'Failed to restore seller product'
          });
        }
      }
    },

    async generateExcel() {
      this.isExcelloading = true;

      // Build query params with both filter and export filter
      const params = new URLSearchParams({
        show_deleted: this.filter.show_deleted,
        is_active: this.filter.is_active || "",
        user_id: this.filter.user_id || "",
        product_id: this.filter.product_id || "",
        search_keyword: this.filter.search_keyword || "",
        // Export filters
        product_variant_id: this.exportFilter.product_variant_id || "",
        brand_name: this.exportFilter.brand_name || "",
        category_id: this.exportFilter.category_id || "",
        tag_id: this.exportFilter.tag_id || "",
        from_date: this.exportFilter.from_date || "",
        to_date: this.exportFilter.to_date || "",
      });

      try {
        const response = await axios.get(`/seller_products/excel?${params}`, {
          responseType: "blob",
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `seller_products_${new Date().toISOString().split("T")[0]}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        // Close dialog after successful export
        this.closeExportDialog();

        // Show success message
        this.$toast.success("Export completed successfully!");
      } catch (error) {
        console.log(error);
        if (error.response && error.response.status === 401) {
          this.$router.push('/login');
        } else {
          this.$store.dispatch('errors/setError', {
            message: 'Failed to export seller products'
          });
        }
      }

      this.isExcelloading = false;
    },

    async getMasters() {
      try {
        const response = await axios.get("/masters?context=seller_product");
        const masters = response.data;

        // Transform users data
        if (masters.users) {
          this.users = masters.users.map(user => ({
            id: user.id,
            name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email,
            email: user.email,
            first_name: user.first_name,
            last_name: user.last_name
          }));
        }

        // Transform products data
        if (masters.products) {
          this.products = masters.products.map(product => ({
            id: product.id,
            name: product.name,
            sku_id: product.sku_id,
            description: product.description
          }));
        }

        // Load categories
        if (masters.categories) {
          this.categories = masters.categories;
        }

        // Load brands
        if (masters.brands) {
          this.brands = masters.brands;
        }
      } catch (error) {
        console.error("Error loading masters:", error);
        if (error.response && error.response.status === 401) {
          this.$router.push("/login");
        } else {
          this.$toast.error("Failed to load dropdown data. Please refresh the page.");
        }
      }
    },

    async loadExportMasters() {
      // Set all loading states to true
      this.loadingProductVariants = true;
      this.loadingBrands = true;
      this.loadingCategories = true;
      this.loadingTags = true;

      try {
        const response = await axios.get("/masters?context=seller_product_export");
        const masters = response.data;

        // Set product variants
        this.productVariants = masters.product_variants || [];

        // Set brands
        this.brands = masters.brands || [];

        // Set categories
        this.categories = masters.categories || [];

        // Set tags
        this.tags = masters.tags || [];

        console.log('Loaded export masters:', {
          productVariants: this.productVariants.length,
          brands: this.brands.length,
          categories: this.categories.length,
          tags: this.tags.length
        });
      } catch (error) {
        console.error("Error loading export masters:", error);
        this.$toast.error("Failed to load export filter options");
      } finally {
        // Reset all loading states
        this.loadingProductVariants = false;
        this.loadingBrands = false;
        this.loadingCategories = false;
        this.loadingTags = false;
      }
    },

    openExportDialog() {
      this.showExportDialog = true;
      // Load all export masters in one API call
      if (this.productVariants.length === 0 || this.brands.length === 0 || this.tags.length === 0) {
        this.loadExportMasters();
      }
    },

    closeExportDialog() {
      this.showExportDialog = false;
    },

    clearExportFilters() {
      this.exportFilter = {
        product_variant_id: null,
        brand_name: null,
        category_id: null,
        tag_id: null,
        from_date: null,
        to_date: null,
      };
    },

    isDeletePage() {
      return this.$route.query.open_trash ? true : false;
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleDateString() + " " + date.toLocaleTimeString();
    },

    clearAllFilters() {
      this.filter.search_keyword = "";
      this.filter.is_active = "";
      this.filter.seller_id = "";
      this.filter.brand_name = "";
      this.filter.category_id = "";
      this.filter.stock_filter = "";
      this.page = 1;
      this.getData();
    },

    getActiveFiltersCount() {
      let count = 0;
      if (this.filter.search_keyword) count++;
      if (this.filter.is_active) count++;
      if (this.filter.seller_id) count++;
      if (this.filter.brand_name) count++;
      if (this.filter.category_id) count++;
      if (this.filter.stock_filter) count++;
      return count;
    },

    changeItemsPerPageToggle() {
      this.itemsPerPage = this.selectedItemsPerPage;
      this.page = 1;
      this.getData();
    },

    // Seller helper methods
    getSellerName(seller) {
      if (!seller) return "N/A";
      return (
        `${seller.first_name || ""} ${seller.last_name || ""}`.trim() ||
        seller.email ||
        "N/A"
      );
    },

    getSellerInitials(seller) {
      if (!seller) return "NA";
      const firstName = seller.first_name || "";
      const lastName = seller.last_name || "";
      return (
        `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || "NA"
      );
    },

    // Stock helper methods
    getStockColor(stock) {
      if (stock <= 0) return "error";
      if (stock <= 10) return "warning";
      if (stock <= 50) return "info";
      return "success";
    },

    getStockTextColor(stock) {
      if (stock <= 0) return "white";
      if (stock <= 10) return "black";
      return "white";
    },

    goToPage() {
      const targetPage = parseInt(this.jumpToPage);
      if (
        targetPage &&
        targetPage >= 1 &&
        targetPage <= this.paginationLength
      ) {
        this.page = targetPage;
      } else {
        // Reset to current page if invalid input
        this.jumpToPage = this.page;
      }
    },

    getImageUrl(imagePath) {
      if (!imagePath) return "";

      // If it's already a full URL, return it
      if (imagePath.startsWith("http")) {
        return imagePath;
      }

      // Use Vultr object storage URL
      const baseUrl = 'https://blr1.vultrobjects.com/space-1/';
      return baseUrl + imagePath.replace(/^\//, '');
    },

    confirmBulkDelete() {
      this.bulkDeleteDialog.show = true;
    },

    async bulkDeleteItems() {
      try {
        this.bulkDeleteDialog.loading = true;

        const ids = this.selectedItems.map((item) => item.id);
        const response = await axios.post("/seller_product_variants/bulk_delete", { ids });

        this.$toast.success(response.data.message || "Selected variants deleted successfully");
        this.selectedItems = [];
        this.bulkDeleteDialog.show = false;
        this.getData();
      } catch (error) {
        console.error("Error bulk deleting items:", error);
        this.$toast.error("Failed to delete selected variants");
      } finally {
        this.bulkDeleteDialog.loading = false;
      }
    },

    // Open upload history dialog
    openUploadHistory() {
      console.log('Opening upload history dialog...');
      this.showUploadHistoryDialog = true;
    },

  },
};
</script>

<style scoped>
/* Same styles as users index page */
.v-container {
  max-width: 100% !important;
}

/* Enhanced Table Styling */
.enhanced-table >>> .v-data-table__wrapper {
  border-radius: 8px;
  font-size: 0.75rem !important;
}

.enhanced-table >>> .v-data-table-header {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  font-weight: 600;
}

.enhanced-table >>> .v-data-table-header th {
  padding: 8px 6px !important;
  border-bottom: 1px solid #e0e0e0 !important;
  font-size: 0.75rem !important;
  height: 36px !important;
}

.enhanced-table >>> tbody tr:hover {
  background: rgba(25, 118, 210, 0.04) !important;
  transition: all 0.1s ease;
}

.enhanced-table >>> tbody tr {
  height: 44px !important;
  transition: all 0.1s ease;
}

.enhanced-table >>> tbody td {
  padding: 6px 6px !important;
  vertical-align: middle !important;
  border-bottom: 1px solid #f5f5f5 !important;
  font-size: 0.75rem !important;
  height: 44px !important;
}

.v-card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95);
}

.v-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.v-btn {
  text-transform: none !important;
  font-weight: 500;
  letter-spacing: 0.2px;
  font-size: 0.75rem !important;
  min-height: 28px !important;
}

.v-btn--x-small {
  height: 24px !important;
  min-width: 48px !important;
  font-size: 0.7rem !important;
  padding: 0 8px !important;
}

.v-chip {
  font-weight: 500;
  letter-spacing: 0.1px;
  font-size: 0.7rem !important;
  height: 20px !important;
}

.v-chip--x-small {
  height: 18px !important;
  font-size: 0.65rem !important;
}

.v-avatar {
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.compact-pagination >>> .v-pagination__item {
  height: 28px !important;
  min-width: 32px !important;
  width: auto !important;
  padding: 0 6px !important;
  font-size: 0.75rem !important;
  margin: 0 1px !important;
}

.compact-input >>> .v-input__control .v-input__slot {
  min-height: 24px !important;
}

.compact-input >>> .v-input__control .v-input__slot input {
  padding: 2px 6px !important;
  font-size: 0.7rem !important;
}

.no-spin-arrows >>> input[type="number"]::-webkit-outer-spin-button,
.no-spin-arrows >>> input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.seller-product-info-card {
  border: 1px solid #e0e0e0;
  transition: all 0.2s ease;
}

.seller-product-info-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
</style>